import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Empty,
  Segmented,
  Space,
  Spin,
  Tag,
  theme,
  Tooltip,
} from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  BypassOverlayMember,
  BypassOverlayReport,
  BypassOverlayRow,
  BypassOverlayView,
} from '@totem/components/portfolio/network/overlayBypass/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { REMOTE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

// Live report of overlay networks that include the "Global Internet Bypass", read straight from the current
// organization's active Conductor connections each time it loads or is refreshed. Read-only.
const BypassOverlayNetworksPanel = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { token } = theme.useToken();
  const [report, setReport] = useState<BypassOverlayReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [view, setView] = useState<BypassOverlayView>('all');
  const [pageSize, setPageSize] = useState<number>(20);

  useEffect(() => {
    setLoading(true);

    setFailed(false);

    fetch(`${REMOTE_ACCESS_ENDPOINT}/overlayNetworks/bypass`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: BypassOverlayReport) => setReport(result))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [userProfile.organization.id, refreshCount, getApiToken]);

  const rows = useMemo(
    (): BypassOverlayRow[] =>
      (report?.connections ?? []).flatMap((connection) =>
        connection.overlays.map((overlay) => ({
          ...overlay,
          key: `${connection.connectionId}-${overlay.id}`,
          connectionName: connection.displayName,
        })),
      ),
    [report],
  );

  const counts = {
    exposed: rows.filter((row) => row.exposed).length,
    enabled: rows.filter((row) => row.enabled).length,
    all: rows.length,
  };
  const trustedDevices = rows.reduce(
    (total, row) => total + (row.enabled ? row.trusted.length : 0),
    0,
  );
  const notFromWindow = rows.filter((row) => !row.maintenanceWindowId).length;
  const visibleRows = rows.filter(
    (row) =>
      view === 'all' ||
      (view === 'exposed' && row.exposed) ||
      (view === 'enabled' && row.enabled),
  );

  const secondary = { color: token.colorTextSecondary, fontSize: 12 };

  const memberTags = (
    members: BypassOverlayMember[],
    color: string,
    limit = 4,
  ) => (
    <Space size={[4, 4]} wrap>
      {members.slice(0, limit).map((member) => (
        <Tooltip key={member.id} title={member.ip || undefined}>
          <Tag color={color} style={{ marginInlineEnd: 0 }}>
            {member.name}
          </Tag>
        </Tooltip>
      ))}
      {members.length > limit && (
        <span style={secondary}>+{members.length - limit} more</span>
      )}
    </Space>
  );

  const stateTag = (row: BypassOverlayRow) => {
    if (row.exposed) {
      return <Tag color="error">Exposed</Tag>;
    }
    if (row.enabled) {
      return (
        <Tooltip title="Communication is on, but no device trusts the bypass">
          <Tag color="warning">On · no trust</Tag>
        </Tooltip>
      );
    }
    return <Tag>Closed</Tag>;
  };

  const columns: ColumnProps<BypassOverlayRow>[] = [
    {
      title: 'Overlay network',
      key: 'name',
      render: (_, row) => (
        <>
          <div style={{ wordBreak: 'break-all' }}>{row.name}</div>
          {row.maintenanceWindowId ? (
            <Link
              to={`/dashboard/maintenanceWindows2/${row.maintenanceWindowId}`}
              style={{ fontSize: 12 }}
            >
              Maintenance window
            </Link>
          ) : (
            <span style={secondary}>
              {row.description || 'Not created by a maintenance window'}
            </span>
          )}
        </>
      ),
      sorter: (compA, compB) => sortStringAscending(compA.name, compB.name),
    },
    {
      title: 'Conductor',
      dataIndex: 'connectionName',
      key: 'connectionName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.connectionName, compB.connectionName),
    },
    {
      title: 'Network communication',
      key: 'enabled',
      render: (_, row) =>
        row.enabled ? <Tag color="processing">On</Tag> : <Tag>Off</Tag>,
      sorter: (compA, compB) => Number(compA.enabled) - Number(compB.enabled),
    },
    {
      title: 'Trusted to bypass',
      key: 'trusted',
      render: (_, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {row.trusted.length > 0 ? (
            memberTags(row.trusted, row.enabled ? 'error' : 'default')
          ) : (
            <span style={secondary}>No device trusts the bypass</span>
          )}
          {(row.blocked.length > 0 || row.untrusted.length > 0) && (
            <span style={secondary}>
              {[
                row.blocked.length > 0 && `${row.blocked.length} blocked`,
                row.untrusted.length > 0 &&
                  `${row.untrusted.length} without trust`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </div>
      ),
      sorter: (compA, compB) => compA.trusted.length - compB.trusted.length,
    },
    {
      title: 'State',
      key: 'state',
      render: (_, row) => stateTag(row),
      defaultSortOrder: 'descend',
      sorter: (compA, compB) =>
        Number(compA.exposed) * 2 +
        Number(compA.enabled) -
        (Number(compB.exposed) * 2 + Number(compB.enabled)),
    },
    {
      title: 'Last changed',
      key: 'updatedAt',
      render: (_, row) =>
        row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-',
      sorter: (compA, compB) =>
        sortStringAscending(compA.updatedAt, compB.updatedAt),
    },
  ];

  const memberList = (
    title: string,
    members: BypassOverlayMember[],
    empty: string,
  ) => (
    <div style={{ minWidth: 220 }}>
      <div
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: token.colorTextSecondary,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {title} ({members.length})
      </div>
      {members.length === 0 ? (
        <span style={secondary}>{empty}</span>
      ) : (
        members.map((member) => (
          <div key={member.id} style={{ fontSize: 13 }}>
            {member.name}
            {member.ip && <span style={secondary}> · {member.ip}</span>}
          </div>
        ))
      )}
    </div>
  );

  if (loading && !report) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ ...secondary, marginTop: 8 }}>
          Reading overlay networks from the Conductor…
        </div>
      </div>
    );
  }

  const connectionNotes = (report?.connections ?? []).filter(
    (connection) => connection.error || !connection.bypassDeviceFound,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span style={secondary}>
          Overlay networks that include the Global Internet Bypass, read live
          from {report?.connections.length ?? 0} active Conductor connection
          {report?.connections.length === 1 ? '' : 's'}
          {report?.generatedAt &&
            ` · checked ${new Date(report.generatedAt).toLocaleTimeString()}`}
          . Visible to Intelligent Buildings users only.
        </span>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => setRefreshCount((count) => count + 1)}
        >
          Refresh from Conductor
        </Button>
      </div>

      {failed && (
        <Alert
          type="error"
          showIcon
          title="The overlay networks could not be read from the Conductor."
        />
      )}
      {connectionNotes.map((connection) => (
        <Alert
          key={connection.connectionId}
          type={connection.error ? 'error' : 'info'}
          showIcon
          title={
            connection.error
              ? `${connection.displayName}: ${connection.error}`
              : `${connection.displayName}: no "Global Internet Bypass" device on this Conductor`
          }
        />
      ))}
      {report && report.connections.length === 0 && (
        <Empty description="This organization has no active Tempered Conductor connection" />
      )}

      {report && report.connections.length > 0 && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 10,
            }}
          >
            <KpiCard
              label="Exposed"
              value={counts.exposed}
              sub="communication on and a device trusts the bypass"
              valueColor={counts.exposed > 0 ? token.colorErrorText : undefined}
              onClick={() => setView('exposed')}
            />
            <KpiCard
              label="Communication on"
              value={counts.enabled}
              sub={`${counts.enabled - counts.exposed} with no trust to the bypass`}
              onClick={() => setView('enabled')}
            />
            <KpiCard
              label="Devices trusted to bypass"
              value={trustedDevices}
              sub="on overlays with communication on"
            />
            <KpiCard
              label="Bypass overlays"
              value={counts.all}
              sub={`${notFromWindow} not created by a maintenance window`}
              onClick={() => setView('all')}
            />
          </div>

          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Segmented<BypassOverlayView>
              value={view}
              onChange={setView}
              options={[
                { value: 'exposed', label: `Exposed ${counts.exposed}` },
                {
                  value: 'enabled',
                  label: `Communication on ${counts.enabled}`,
                },
                { value: 'all', label: `All ${counts.all}` },
              ]}
            />
          </div>

          <IBTable
            showSorterTooltip
            columns={columns}
            dataSource={visibleRows}
            loading={loading}
            rowKey={(row) => row.key}
            scroll={{ x: true }}
            locale={{
              emptyText:
                view === 'exposed'
                  ? 'No overlay is exposing devices to the internet'
                  : 'No overlay networks include the Global Internet Bypass',
            }}
            expandable={{
              expandedRowRender: (row) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                  {memberList(
                    'Trusted to bypass',
                    row.trusted,
                    'No device trusts the bypass',
                  )}
                  {memberList('Trust blocked', row.blocked, 'None')}
                  {memberList('In overlay, no trust', row.untrusted, 'None')}
                </div>
              ),
            }}
            pagination={{
              pageSize,
              showSizeChanger: true,
              onShowSizeChange: (_, size) => setPageSize(size),
            }}
          />
        </>
      )}
    </div>
  );
};

export default BypassOverlayNetworksPanel;
